import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { message } from 'antd';
import communityAPI from '../api';

const DEFAULT_VIEW_TTL_MS = 30 * 60 * 1000; // 30 minutes

const getViewStorageKey = (slugValue) => `community:viewed:${slugValue}`;

const canUseLocalStorage = () => {
  try {
    return typeof window !== 'undefined' && Boolean(window.localStorage);
  } catch (err) {
    return false;
  }
};

const shouldIncrementView = (slugValue, ttlMs) => {
  if (!slugValue || ttlMs <= 0 || !canUseLocalStorage()) {
    return true;
  }
  const key = getViewStorageKey(slugValue);
  const raw = window.localStorage.getItem(key);
  const lastSeen = raw ? Number(raw) : 0;
  if (Number.isFinite(lastSeen) && lastSeen > 0) {
    const age = Date.now() - lastSeen;
    if (age >= 0 && age < ttlMs) {
      return false;
    }
  }
  return true;
};

const markViewAttempt = (slugValue) => {
  if (!slugValue || !canUseLocalStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(getViewStorageKey(slugValue), Date.now().toString());
  } catch (err) {
    // ignore storage failures
  }
};

const clearViewAttempt = (slugValue) => {
  if (!slugValue || !canUseLocalStorage()) {
    return;
  }
  try {
    window.localStorage.removeItem(getViewStorageKey(slugValue));
  } catch (err) {
    // ignore storage failures
  }
};

export const usePost = (slug, { skipViewIncrement = false, viewTtlMs = DEFAULT_VIEW_TTL_MS } = {}) => {
  const queryClient = useQueryClient();
  const incrementInFlight = useRef(false);

  const updateViewsEverywhere = (slugValue, nextViews) => {
    if (!slugValue || typeof nextViews !== 'number') {
      return;
    }

    const patchItem = (item) => {
      if (!item || typeof item !== 'object') {
        return item;
      }
      if (item.slug === slugValue) {
        return { ...item, views: nextViews };
      }
      return item;
    };

    const patchData = (data) => {
      if (!data) {
        return data;
      }

      if (Array.isArray(data)) {
        return data.map(patchItem);
      }

      if (typeof data !== 'object') {
        return data;
      }

      // Post detail object
      if (data.slug === slugValue) {
        return { ...data, views: nextViews };
      }

      // Normalized list response: { data: [...], meta: {...} }
      if (Array.isArray(data.data)) {
        return { ...data, data: data.data.map(patchItem) };
      }

      // Infinite query shape: { pages: [{data:[...]}, ...], pageParams: [...] }
      if (Array.isArray(data.pages)) {
        return {
          ...data,
          pages: data.pages.map((page) => patchData(page))
        };
      }

      return data;
    };

    queryClient.setQueriesData(
      {
        predicate: (query) => {
          const key = query?.queryKey;
          return Array.isArray(key) && key[0] === 'community' && key[1] === 'posts';
        }
      },
      (oldData) => patchData(oldData)
    );
  };

  useEffect(() => {
    incrementInFlight.current = false;
  }, [slug]);

  const query = useQuery({
    queryKey: ['community', 'posts', 'detail', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const response = await communityAPI.posts.getBySlug(slug);
      return response.data;
    },
    staleTime: 30_000,
    onError: (err) => {
      if (err?.message) {
        message.error('Failed to fetch post');
      }
    }
  });

  useEffect(() => {
    if (skipViewIncrement || !slug) {
      return;
    }
    // Ensure the post exists (avoids incrementing for invalid slugs / errors)
    if (!query.isSuccess || !query.data) {
      return;
    }
    if (incrementInFlight.current) {
      return;
    }
    if (!shouldIncrementView(slug, viewTtlMs)) {
      return;
    }

    incrementInFlight.current = true;
    markViewAttempt(slug);

    (async () => {
      try {
        const response = await communityAPI.posts.incrementView(slug);
        const nextViews = response?.data?.views;
        if (typeof nextViews === 'number') {
          updateViewsEverywhere(slug, nextViews);
        }
      } catch (err) {
        // Allow a later retry if the increment call failed.
        clearViewAttempt(slug);
        incrementInFlight.current = false;
      }
    })();
  }, [query.isSuccess, query.data, skipViewIncrement, slug, viewTtlMs, queryClient]);

  return {
    post: query.data ?? null,
    loading: query.isPending,
    error: query.error ?? null,
    refetch: query.refetch
  };
};

export default usePost;
